import { yupResolver } from '@hookform/resolvers/yup';
import Fuse from 'fuse.js';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import {
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import {
  FlatList,
  SafeAreaView,
  Text,
  TextInput as TextInput1,
  TouchableOpacity,
  View,
  ScrollView,
} from 'react-native';
import Icon from '@react-native-vector-icons/fontawesome';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { ShowToast, storeSetting, retrieveSetting } from '../Common/common';
import BTButton from '../Components/BTButton';
import BTComboBox from '../Components/BTComboBox';
import { Col, Row } from '../Components/BTGrid';
import { TextInput } from '../Components/BTTextInput';
import {
  useInsertFPEProductionMutation,
  useUpdateFPEProductionMutation,
  useGetFPEProductionByIdQuery,
  fp_eproduction,
  fp_eproductiondetails,
  fp_econsumption,
} from '../redux/services/fpeproductionApi';
import { useGetPOMProductsQuery } from '../redux/services/pomproductApi';
import { useGetPOMGroupsQuery } from '../redux/services/pomgroupApi';
import { useGetPOMKitchensQuery } from '../redux/services/pomkitchenApi';
import { useGetPOMReceipesQuery } from '../redux/services/pomreceipeApi';
import { RootState } from '../redux/store';
import styles from '../styles/Styles';

import T from '../Components/T';
import { tp } from '../utils/translateText';
interface ProductItem {
  pr_group?: number;
  pr_id: number;
  pr_name?: string;
  pr_rate: number;
  pr_qty: number;
  pr_grname?: string;
  pr_recipe?: number;
  pr_hasrecipe?: boolean;
  value: any;
}

interface CartItem {
  product_id: number;
  product_name: string;
  recipe_id: number;
  planned_qty: number;
  actual_qty: number;
  wastage_qty: number;
  unit_cost: number;
  total_cost: number;
  sale_price: number;
  consumption: ConsumptionItem[];
}

interface ConsumptionItem {
  fc_product: number;
  fc_product_name?: string;
  fc_expected_qty: number;
  fc_actual_qty: number;
  fc_rate: number;
  fc_amount: number;
}

interface CartState {
  [key: string]: CartItem;
}

const validationSchema = yup.object().shape({
  fp_kitchen: yup
    .number()
    .required()
    .typeError('Select Kitchen?')
    .min(1, 'Select Kitchen'),
  fp_status: yup
    .number()
    .required()
    .typeError('Select Status?')
    .min(0, 'Select Status'),
});

const FPProductionEntry = ({ navigation, route }: UniversalProps) => {
  const inputRef = useRef(null);
  const gdata: any = useSelector((state: RootState) => state.gdata);
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState(0);
  const [cart, setCart] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [cartItems, setCartItems] = useState<CartState>({});
  const [selectedKitchen, setSelectedKitchen] = useState<any>(null);
  const [showHead, setShowHead] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState<number>(0);

  const cid = gdata.company;
  const olid = gdata.outlet || 0;

  // Local storage keys
  const KITCHEN_STORAGE_KEY = `fp_selected_kitchen_${cid}`;

  // Store selected kitchen
  const storeSelectedKitchen = async (kitchen: any) => {
    try {
      await storeSetting(KITCHEN_STORAGE_KEY, JSON.stringify(kitchen));
      setSelectedKitchen(kitchen);
    } catch (error) {
      console.error('Error storing selected kitchen:', error);
    }
  };

  const loadSelectedKitchen = async () => {
    try {
      const stored = await retrieveSetting(KITCHEN_STORAGE_KEY);
      if (stored) {
        const kitchen = JSON.parse(stored);
        setSelectedKitchen(kitchen);
        return kitchen;
      }
    } catch (error) {
      console.error('Error loading selected kitchen:', error);
    }
    return null;
  };

  // API Queries
  const { data: productData } = useGetPOMProductsQuery({ cid, olid });
  const { data: groupData } = useGetPOMGroupsQuery({ cid, olid });
  const { data: kitchenData } = useGetPOMKitchensQuery(cid);
  const { data: recipeData } = useGetPOMReceipesQuery(cid);

  // Load selected kitchen on mount
  useEffect(() => {
    loadSelectedKitchen();
  }, []);

  // Update form value when selected kitchen changes
  useEffect(() => {
    if (selectedKitchen && methods) {
      try {
        methods.setValue('fp_kitchen', selectedKitchen.kt_id);
      } catch (error) {
        console.error('Error setting kitchen value:', error);
      }
    }
  }, [selectedKitchen]);

  const handleKitchenChange = (selectedValue: any) => {
    try {
      const selectedKitchenData = kitchenData?.find(
        (k: any) => k.kt_id === selectedValue,
      );
      if (selectedKitchenData) {
        storeSelectedKitchen(selectedKitchenData);
        if (methods) {
          methods.setValue('fp_kitchen', selectedValue);
        }
      }
    } catch (error) {
      console.error('Error handling kitchen change:', error);
    }
  };

  // Prepare dropdown data
  const kitchens: any = kitchenData?.map((value: any) => {
    return {
      label: value.kt_name,
      id: value.kt_id,
      values: value,
    };
  });

  const groups: any = groupData?.map((value: any) => {
    return {
      label: value.gr_name,
      id: value.gr_id,
      values: value,
    };
  });

  const statusOptions = [
    { label: 'Planned', id: 0 },
    { label: 'In-Progress', id: 1 },
    { label: 'Completed', id: 2 },
    { label: 'Cancelled', id: 3 },
  ];

  // Prepare products with recipe info
  const products1: any = productData?.map((value: any) => {
    const hasRecipe = recipeData?.some(
      (r: any) => r.re_product === value.pr_id,
    );
    const recipeId = recipeData?.find(
      (r: any) => r.re_product === value.pr_id,
    )?.re_id;

    return {
      pr_group: value.pr_group,
      pr_id: value.pr_id,
      pr_name: value.pr_name,
      pr_rate: value.pr_price1 || 0,
      pr_qty: 0,
      pr_grname: value.gr_name,
      pr_hasrecipe: hasRecipe,
      pr_recipe: recipeId || 0,
      value: value,
    };
  });

  const [products, setProducts] = useState(products1);

  // Filter and search products
  useEffect(() => {
    const prods = products1?.filter((v: any) => {
      return Number(v.pr_group) == Number(group) || Number(group) == 0;
    });

    if (search !== '' && !isNaN(parseFloat(search)) && !cart) {
      const d = products1?.filter((o: any) => o.value.pr_code == search);
      setProducts(d);
    } else if (search.length >= 3 && !cart) {
      const options = {
        includeScore: true,
        useExtendedSearch: true,
        keys: ['pr_name'],
      };

      const fuse = new Fuse(prods, options);
      const b = fuse.search(search);
      const c = b.map((v: any) => {
        return v.item;
      });

      setProducts(c);
    } else if (search == '') {
      setProducts(
        prods?.filter((v: any) => {
          return (cartItems?.hasOwnProperty(v.pr_id) && cart) || !cart;
        }),
      );
    }
  }, [search, productData, cart, group, cartItems]);

  // Handle adding/removing products to cart
  const handleCart = (item: ProductItem, plannedQty: number) => {
    if (!item.pr_hasrecipe) {
      ShowToast('This product does not have a recipe', 'warning');
      return;
    }

    const existingItem = cartItems[item.pr_id];
    const newPlannedQty = (existingItem?.planned_qty || 0) + plannedQty;

    if (newPlannedQty < 0) {
      // Remove item
      const newCart = { ...cartItems };
      delete newCart[item.pr_id];
      setCartItems(newCart);
      return;
    }

    // Get recipe details for consumption
    const recipe = recipeData?.find((r: any) => r.re_product === item.pr_id);
    let consumption: ConsumptionItem[] = [];

    if (recipe && recipe.re_details) {
      consumption = recipe.re_details.map((detail: any) => {
        // Look up ingredient product info for rate and name
        const ingredientProduct = productData?.find(
          (p: any) => p.pr_id === detail.rd_product,
        );
        const rate = Number(ingredientProduct?.pr_prate || 0);
        const name = ingredientProduct?.pr_name || '';
        const qty = Number(detail.rd_qty);
        
        return {
          fc_product: Number(detail.rd_product),
          fc_product_name: name,
          fc_expected_qty: qty * newPlannedQty,
          fc_actual_qty: qty * newPlannedQty,
          fc_rate: rate,
          fc_amount: qty * newPlannedQty * rate,
        };
      });
    }

    const unitCost =
      consumption.reduce((sum, c) => sum + c.fc_amount, 0) /
      (newPlannedQty || 1);
    const totalCost = consumption.reduce((sum, c) => sum + c.fc_amount, 0);

    setCartItems({
      ...cartItems,
      [item.pr_id]: {
        product_id: item.pr_id,
        product_name: item.pr_name || '',
        recipe_id: item.pr_recipe || 0,
        planned_qty: newPlannedQty,
        actual_qty: existingItem?.actual_qty || newPlannedQty,
        wastage_qty: 0,
        unit_cost: unitCost,
        total_cost: totalCost,
        sale_price: item.pr_rate,
        consumption: consumption,
      },
    });
  };

  const handleCartClick = () => {
    setCart(!cart);
    setGroup(0);
    setSearch('');
  };

  const updateCartItemQty = (
    itemId: string,
    field: 'planned_qty' | 'actual_qty',
    value: number,
  ) => {
    const item = cartItems[itemId];
    if (!item) return;

    const newPlannedQty = field === 'planned_qty' ? value : item.planned_qty;
    const newActualQty = field === 'actual_qty' ? value : item.actual_qty;
    const wastageQty = newPlannedQty - newActualQty;

    // Recalculate consumption based on actual qty
    const recipe = recipeData?.find(
      (r: any) => r.re_product === item.product_id,
    );
    let consumption: ConsumptionItem[] = item.consumption;

    if (recipe && recipe.re_details && field === 'planned_qty') {
      consumption = recipe.re_details.map((detail: any) => {
        // Look up ingredient product info for rate and name
        const ingredientProduct = productData?.find(
          (p: any) => p.pr_id === detail.rd_product,
        );
        const rate = Number(ingredientProduct?.pr_prate || 0);
        const name = ingredientProduct?.pr_name || '';
        const qty = Number(detail.rd_qty);
        
        return {
          fc_product: Number(detail.rd_product),
          fc_product_name: name,
          fc_expected_qty: qty * newPlannedQty,
          fc_actual_qty: qty * newPlannedQty,
          fc_rate: rate,
          fc_amount: qty * newPlannedQty * rate,
        };
      });
    }

    const totalCost = consumption.reduce((sum, c) => sum + c.fc_amount, 0);
    const unitCost = totalCost / (newPlannedQty || 1);

    setCartItems({
      ...cartItems,
      [itemId]: {
        ...item,
        planned_qty: newPlannedQty,
        actual_qty: newActualQty,
        wastage_qty: wastageQty,
        unit_cost: unitCost,
        total_cost: totalCost,
        consumption: consumption,
      },
    });
  };

  // Form initial values
  let initialValues: any = {
    fp_id: 0,
    fp_company: cid,
    fp_date:
      moment(gdata.today).format('YYYY/MM/DD') + moment().format(' HH:mm:ss'),
    fp_no: '',
    fp_kitchen: selectedKitchen?.kt_id || '',
    fp_outlet: olid,
    fp_status: 0,
    fp_remarks: '',
    fp_uid: gdata.user,
    fp_cdt: moment().format('YYYY/MM/DD HH:mm:ss'),
    fp_muid: gdata.user,
    fp_mdt: moment().format('YYYY/MM/DD HH:mm:ss'),
    fp_details: [],
  };

  const { ...methods } = useForm<any>({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  const [insertFPEProduction] = useInsertFPEProductionMutation();
  const [updateFPEProduction] = useUpdateFPEProductionMutation();

  const onSubmit: SubmitHandler<fp_eproduction> = async values => {
    if (isSaving) {
      return;
    }

    try {
      let details: fp_eproductiondetails[] = [];
      let srid = 1;

      for (const [id, item] of Object.entries(cartItems) as [
        string,
        CartItem,
      ][]) {
        if (item.planned_qty > 0) {
          const consumptionDetails: fp_econsumption[] = item.consumption.map(
            (c: ConsumptionItem) => ({
              fc_id: 0,
              fc_fpid: 0,
              fc_fdid: 0,
              fc_product: c.fc_product,
              fc_expected_qty: c.fc_expected_qty,
              fc_actual_qty: c.fc_actual_qty,
              fc_rate: c.fc_rate,
              fc_amount: c.fc_amount,
            }),
          );

          details.push({
            fd_id: 0,
            fd_fpid: 0,
            fd_srid: srid++,
            fd_product: item.product_id,
            fd_recipe: item.recipe_id,
            fd_planned_qty: item.planned_qty,
            fd_actual_qty: item.actual_qty,
            fd_wastage_qty: item.wastage_qty,
            fd_unit_cost: item.unit_cost,
            fd_total_cost: item.total_cost,
            fd_sale_price: item.sale_price,
            fd_consumption: consumptionDetails,
          });
        }
      }

      values.fp_details = details;
      values.fp_date = new Date();

      if (values.fp_details.length === 0) {
        ShowToast('Add at least one product with recipe', 'error');
        return;
      }

      setIsSaving(true);

      if (Number(values.fp_id) !== 0) {
        const dat: any = await updateFPEProduction(values).unwrap();
        resetForm();
        ShowToast(dat.fp_no + ' Updated Successfully');
      } else {
        const dat: any = await insertFPEProduction(values).unwrap();
        resetForm();
        ShowToast(dat.fp_no + ' Saved Successfully');
      }

      setIsSaving(false);
      navigation.goBack();
    } catch (e: any) {
      ShowToast(e?.data?.message || e?.message || 'Error saving production', 'error');
      setIsSaving(false);
    }
  };

  const onError: SubmitErrorHandler<fp_eproduction> = (errors, e) => {
    Object.keys(errors).map((fieldName: string) => {
      const error: any = errors[fieldName as keyof typeof errors];
      if (error?.message) {
        ShowToast(error.message, 'error');
      }
    });
    return false;
  };

  const resetForm = () => {
    try {
      if (methods) {
        methods.reset({
          ...initialValues,
          fp_kitchen: selectedKitchen?.kt_id || '',
        });
      }
      setCartItems({});
    } catch (error) {
      console.error('Error resetting form:', error);
    }
  };

  // Calculate totals
  const [totals, setTotals] = useState({
    totalPlanned: 0,
    totalActual: 0,
    totalWastage: 0,
    totalCost: 0,
    totalSaleValue: 0,
    totalMargin: 0,
    marginPercent: 0,
  });

  useEffect(() => {
    let planned = 0,
      actual = 0,
      wastage = 0,
      cost = 0,
      saleValue = 0;

    for (const [_id, item] of Object.entries(cartItems) as [
      string,
      CartItem,
    ][]) {
      planned += item.planned_qty;
      actual += item.actual_qty;
      wastage += item.wastage_qty;
      cost += item.total_cost;
      saleValue += item.sale_price * item.actual_qty;
    }

    const margin = saleValue - cost;
    const marginPercent = cost > 0 ? (margin / cost) * 100 : 0;

    setTotals({
      totalPlanned: planned,
      totalActual: actual,
      totalWastage: wastage,
      totalCost: cost,
      totalSaleValue: saleValue,
      totalMargin: margin,
      marginPercent: marginPercent,
    });
  }, [cartItems]);

  const renderSeparator = () => {
    return (
      <View
        style={{
          height: 1,
          width: '86%',
          backgroundColor: '#CED0CE',
          marginLeft: '14%',
        }}
      />
    );
  };

  const renderProductItem = (item: ProductItem) => {
    return item ? (
      <View style={styles.conLstItem} key={item.pr_id}>
        <View style={styles.conLstData}>
          {group == 0 ? (
            <Row>
              <Col>
                <Text style={styles.descr}>{item?.pr_grname}</Text>
              </Col>
            </Row>
          ) : null}
          <Row>
            <Col>
              <Text style={styles.heading}>{item?.pr_name}</Text>
              {item.pr_hasrecipe ? (
                <Text style={{ fontSize: 10, color: '#28A745' }}><T>✓ Recipe</T></Text>
              ) : (
                <Text style={{ fontSize: 10, color: '#FF6B6B' }}>
                  <T>✗ No Recipe</T>
                </Text>
              )}
            </Col>
          </Row>

          <Row style={styles.rowSec}>
            <Col>
              <Text style={styles.rate}>{'Rs. ' + item?.pr_rate}</Text>
            </Col>
            <Col style={styles.iconCol}>
              <TouchableOpacity
                onPress={() => handleCart(item, -1)}
                disabled={!item.pr_hasrecipe}
                style={{
                  backgroundColor: item.pr_hasrecipe ? '#FF6B6B' : '#CCC',
                  padding: 1,
                  borderRadius: 50,
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 4 },
                  shadowOpacity: 0.3,
                  shadowRadius: 4,
                  elevation: 6,
                }}
              >
                <Icon
                  name="minus-circle"
                  size={42}
                  color="#FFF"
                  style={{ alignSelf: 'center' }}
                />
              </TouchableOpacity>
            </Col>
            <Col>
              <Text style={styles.qty}>
                {cartItems[item.pr_id]?.planned_qty || 0}
              </Text>
            </Col>
            <Col style={styles.iconCol}>
              <TouchableOpacity
                onPress={() => handleCart(item, 1)}
                disabled={!item.pr_hasrecipe}
                style={{
                  backgroundColor: item.pr_hasrecipe ? '#28A745' : '#CCC',
                  padding: 1,
                  borderRadius: 50,
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 4 },
                  shadowOpacity: 0.3,
                  shadowRadius: 4,
                  elevation: 6,
                }}
              >
                <Icon
                  name="plus-circle"
                  size={42}
                  color="#FFF"
                  style={{ alignSelf: 'center' }}
                />
              </TouchableOpacity>
            </Col>
          </Row>
        </View>
      </View>
    ) : (
      <View />
    );
  };

  const renderCartItem = (itemId: string, item: CartItem) => {
    return (
      <View
        key={itemId}
        style={{
          padding: 10,
          marginVertical: 5,
          backgroundColor: '#f9f9f9',
          borderRadius: 8,
          borderWidth: 1,
          borderColor: '#ddd',
        }}
      >
        <Row>
          <Col>
            <Text style={[styles.heading, { fontSize: 14 }]}>
              {item.product_name}
            </Text>
          </Col>
          <Col style={{ alignItems: 'flex-end' }}>
            <TouchableOpacity
              onPress={() =>
                handleCart(
                  {
                    pr_id: item.product_id,
                    pr_group: 0,
                    pr_rate: item.sale_price,
                    pr_qty: 0,
                    pr_hasrecipe: true,
                    pr_recipe: item.recipe_id,
                    pr_name: item.product_name,
                    value: {},
                  },
                  -item.planned_qty,
                )
              }
            >
              <Icon name="times-circle" size={24} color="#FF6B6B" />
            </TouchableOpacity>
          </Col>
        </Row>
        <Row>
          <Col>
            <Text style={{ fontSize: 11, color: '#666' }}><T>Planned Qty:</T></Text>
            <TextInput1
              style={{
                borderWidth: 1,
                borderColor: '#ccc',
                padding: 5,
                borderRadius: 4,
                marginTop: 2,
              }}
              keyboardType="numeric"
              value={item.planned_qty.toString()}
              onChangeText={txt =>
                updateCartItemQty(itemId, 'planned_qty', Number(txt) || 0)
              }
            />
          </Col>
          <Col>
            <Text style={{ fontSize: 11, color: '#666' }}><T>Actual Qty:</T></Text>
            <TextInput1
              style={{
                borderWidth: 1,
                borderColor: '#ccc',
                padding: 5,
                borderRadius: 4,
                marginTop: 2,
              }}
              keyboardType="numeric"
              value={item.actual_qty.toString()}
              onChangeText={txt =>
                updateCartItemQty(itemId, 'actual_qty', Number(txt) || 0)
              }
            />
          </Col>
          <Col>
            <Text style={{ fontSize: 11, color: '#666' }}><T>Wastage:</T></Text>
            <Text style={{ fontSize: 14, fontWeight: 'bold', marginTop: 5 }}>
              {item.wastage_qty}
            </Text>
          </Col>
        </Row>
        <Row style={{ marginTop: 5 }}>
          <Col>
            <Text style={{ fontSize: 10, color: '#888' }}>
              <T>Cost: ₹</T>{item.total_cost.toFixed(2)}
            </Text>
          </Col>
          <Col>
            <Text style={{ fontSize: 10, color: '#888' }}>
              <T>Sale: ₹</T>{(item.sale_price * item.actual_qty).toFixed(2)}
            </Text>
          </Col>
        </Row>
        {item.consumption.length > 0 && (
          <View
            style={{
              marginTop: 5,
              paddingTop: 5,
              borderTopWidth: 1,
              borderTopColor: '#eee',
            }}
          >
            <Text style={{ fontSize: 10, fontWeight: 'bold', color: '#555' }}>
              <T>Ingredients:</T>
            </Text>
            {item.consumption.map((c, idx) => (
              <Text key={idx} style={{ fontSize: 9, color: '#666' }}>
                • {c.fc_product_name}: {c.fc_actual_qty.toFixed(2)} @ ₹
                {c.fc_rate.toFixed(2)} = ₹{c.fc_amount.toFixed(2)}
              </Text>
            ))}
          </View>
        )}
      </View>
    );
  };

  return (
    <View style={styles.app}>
      {/* Header */}
      <View style={styles.modernHeaderContainer}>
        <Text style={styles.modernHeaderTitle}><T>Food Production Entry</T></Text>
      </View>

      {selectedKitchen && (
        <View
          style={{
            backgroundColor: '#d4edda',
            padding: 8,
            borderBottomWidth: 1,
            borderBottomColor: '#c3e6cb',
          }}
        >
          <Text style={{ color: '#155724', fontSize: 12, textAlign: 'center' }}>
            <T>Last used kitchen:</T> {selectedKitchen?.kt_name || 'N/A'}
          </Text>
        </View>
      )}

      <FormProvider {...methods}>
        {showHead ? (
            <>
              <Row>
                <Col>
                  <BTComboBox
                    name="fp_kitchen"
                    label="Kitchen"
                    data={kitchens}
                    placeholder={tp("Select Kitchen")}
                    onValChange={handleKitchenChange}
                  />
                </Col>
                <Col>
                  <BTComboBox
                    name="fp_status"
                    label="Status"
                    data={statusOptions}
                    placeholder={tp("Select Status")}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <TextInput
                    name="fp_remarks"
                    label="Remarks"
                    placeholder={tp("Remarks")}
                  />
                </Col>
              </Row>
            </>
          ) : null}

          <Row>
            <Col>
              <View style={styles.button}>
                <BTButton
                  title="Save"
                  color="#146C94"
                  onPress={methods.handleSubmit(onSubmit, onError)}
                  onLongPress={() => {}}
                  disabled={isSaving}
                />
              </View>
            </Col>
            <Col>
              <View style={styles.button}>
                <BTButton
                  title="Clear"
                  color="#146C94"
                  onPress={resetForm}
                  onLongPress={() => {}}
                  disabled={isSaving}
                />
              </View>
            </Col>
            <Col>
              <View style={styles.button}>
                <BTButton
                  title="Cart"
                  color={cart ? '#19A7CE' : '#146C94'}
                  onPress={() => handleCartClick()}
                  onLongPress={() => {}}
                  disabled={isSaving}
                />
              </View>
            </Col>
            <Col>
              <View style={styles.button}>
                <BTButton
                  title={showHead ? 'Hide' : 'Form'}
                  onLongPress={() => {}}
                  color={showHead ? '#19A7CE' : '#146C94'}
                  onPress={() => setShowHead(prev => !prev)}
                  disabled={isSaving}
                />
              </View>
            </Col>
          </Row>

          {cart ? (
            <View style={{ padding: 10 }}>
              <Row>
                <Col>
                  <Text style={[styles.heading, { fontSize: 12 }]}>
                    <T>Planned:</T> {totals.totalPlanned}
                  </Text>
                  <Text style={[styles.heading, { fontSize: 12 }]}>
                    <T>Actual:</T> {totals.totalActual}
                  </Text>
                  <Text
                    style={[styles.heading, { fontSize: 12, color: '#FF6B6B' }]}
                  >
                    <T>Wastage:</T> {totals.totalWastage}
                  </Text>
                </Col>
                <Col>
                  <Text style={{ fontSize: 11 }}>
                    <T>Cost: ₹</T>{totals.totalCost.toFixed(2)}
                  </Text>
                  <Text style={{ fontSize: 11 }}>
                    <T>Sale: ₹</T>{totals.totalSaleValue.toFixed(2)}
                  </Text>
                  <Text
                    style={{
                      fontSize: 11,
                      color: totals.totalMargin >= 0 ? '#28A745' : '#FF6B6B',
                    }}
                  >
                    <T>Margin: ₹</T>{totals.totalMargin.toFixed(2)} (
                    {totals.marginPercent.toFixed(1)}%)
                  </Text>
                </Col>
              </Row>
            </View>
          ) : null}

          {cart ? null : (
            <Row>
              <Col>
                <BTComboBox
                  name="group"
                  label="Group"
                  data={groups}
                  placeholder={tp("Select Group")}
                  onValChange={(v: any) => setGroup(v)}
                />
              </Col>
            </Row>
          )}
        </FormProvider>

      {cart ? null : (
        <Row>
          <Col>
            <TextInput1
              style={styles.text}
              onChangeText={setSearch}
              value={search}
              placeholder={tp("Search Products")}
            />
          </Col>
        </Row>
      )}

      <View style={{ flex: 1 }}>
        {cart ? (
          <ScrollView
            contentContainerStyle={{
              paddingBottom: 80,
              paddingHorizontal: 10,
            }}
          >
            {Object.entries(cartItems).map(([id, item]) =>
              renderCartItem(id, item as CartItem),
            )}
          </ScrollView>
        ) : (
          <FlatList
            ItemSeparatorComponent={renderSeparator}
            data={products}
            renderItem={({ item }) => renderProductItem(item)}
            onEndReachedThreshold={0.1}
            contentContainerStyle={{ paddingBottom: 80 }}
            keyboardShouldPersistTaps="handled"
          />
        )}
      </View>
    </View>
  );
};

export default FPProductionEntry;
